// LivestreamOverlayCta.jsx
import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import LivestreamOverlayFooter from '../container/LivestreamOverlayFooter.js';
import UserAvatar from '../container/UserAvatar.js';
import WithLivestreamRating from '../container/WithLivestreamRating.js';
import DecryptionWrapper from '../container/DecryptionWrapper.js';

import TriggerPrivateNotificationModal from '../component/TriggerPrivateNotificationModal.jsx';
import Rating from '../component/Rating.jsx';
import LivestreamOverlaySwiper from '../component/LivestreamOverlaySwiper.jsx';
import { SwiperSlide } from '../component/Swiper.jsx';
import LivestreamOverlayChatroom from '../container/LivestreamOverlayChatroom.js';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';
import { ModalId, ButtonId } from '../resource/mixpanel.js';
import { LivestreamMode } from '../resource/liveStreamConstants.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { getUserPicture, MediaAssetFormat } from '../resource/getMediaAsset.js';

import { color, breakpoint } from '../style/variables.js';
import media from '../style/media.js';

export const LivestreamOverlayCta = ({
  streamId = null,
  meId = null,
  username = '',
  displayName = '',
  streamTitle = '',
  categories = EMPTY_ARRAY,
  balance = 0,
  mode = LivestreamMode.FREE,
  exclusivePrice = null,
  addvalue = NULL_FUNCTION,
  unlockPrivateStream = NULL_FUNCTION,
  fetchStreamsToken = NULL_FUNCTION,
  setModalTracking = NULL_FUNCTION,
  sessionId = '',
  isLiveStreamSnapshotEnabled = false,
  publicUrlHostname = '',
  isExclusivePrivatePerforming = false,
  isExclusivePrivatePreparing = false,
  isMeExclusivePrivateBuyer = false,
  hasStreamToken = false,
  isFetchingStreamToken = false,
  deviceWidth = 0,
}) => {
  const { t } = useTranslation();
  const isInsufficientDiamondForExclusivePrivate = balance < exclusivePrice;
  const mixpanelModalId =
    LivestreamMode.ONE_VS_ONE === mode
      ? isInsufficientDiamondForExclusivePrivate
        ? ModalId.LiveStream.ModalExclusivePrivatePurchaseDiamond
        : ModalId.LiveStream.ModalEnterExclusivePrivate
      : LivestreamMode.SHOW === mode
        ? ModalId.LiveStream.LivestreamShowPurchaseModal
        : LivestreamMode.ONE_VS_N === mode
          ? ModalId.LiveStream.PrivateLivestreamDetail
          : undefined;
  const isExclusivePrivateOngoing =
    isExclusivePrivatePerforming || isExclusivePrivatePreparing;
  const shouldShowSnapshot =
    !!sessionId && isLiveStreamSnapshotEnabled && !isExclusivePrivateOngoing;
  const snapshotSource = useMemo(() => {
    if (!shouldShowSnapshot) return null;
    return getResourceUrl({
      endpoint: `/sessions/${sessionId}/snapshot?hostname=${publicUrlHostname}`,
    }).href;
  }, [publicUrlHostname, sessionId, shouldShowSnapshot]);
  const userPictureSource = useMemo(() => {
    if (!streamId) return null;
    return getUserPicture({
      userId: streamId,
      meId,
      size: 512,
      format: MediaAssetFormat.JPG,
    }).href;
  }, [meId, streamId]);
  const coverSource = shouldShowSnapshot ? snapshotSource : userPictureSource;

  // Set modal tracking
  useEffect(() => {
    setModalTracking({
      modalId: mixpanelModalId,
    });
  }, [mixpanelModalId, setModalTracking]);

  useEffect(() => {
    const shouldFetchStreamToken =
      LivestreamMode.ONE_VS_ONE === mode &&
      !hasStreamToken &&
      !isFetchingStreamToken &&
      isExclusivePrivateOngoing &&
      isMeExclusivePrivateBuyer &&
      !!streamId;

    if (shouldFetchStreamToken) {
      fetchStreamsToken({ streamId });
    }
  }, [
    fetchStreamsToken,
    hasStreamToken,
    isExclusivePrivateOngoing,
    isMeExclusivePrivateBuyer,
    isFetchingStreamToken,
    mode,
    streamId,
  ]);

  if (
    ![
      LivestreamMode.ONE_VS_ONE,
      LivestreamMode.SHOW,
      LivestreamMode.ONE_VS_N,
    ].includes(mode)
  ) {
    return null;
  }

  if (LivestreamMode.ONE_VS_ONE === mode) {
    return (
      <TriggerPrivateNotificationModal
        titleI18nKey="stream_exclusive_waiting"
        body={
          <FlexCenterBox>
            <UserAvatarRow>
              <UserAvatarWrapper>
                <UserAvatar id={streamId} size={64} />
              </UserAvatarWrapper>
              <UserAvatarWrapper>
                <UserAvatar id={meId} size={64} />
              </UserAvatarWrapper>
            </UserAvatarRow>
            <UserDisplayName>{displayName || username}</UserDisplayName>
          </FlexCenterBox>
        }
        button={
          <PurchaseDiamondButton
            type="button"
            data-element_id={
              isInsufficientDiamondForExclusivePrivate
                ? ButtonId.LiveStream.LivestreamShowPurchaseModal
                : ButtonId.LiveStream.ButtonEnterExclusivePrivate
            }
            data-tracking_payload={{
              modalId: mixpanelModalId,
            }}
            disabled={isFetchingStreamToken}
            onClick={() => {
              if (isFetchingStreamToken) return;
              return isInsufficientDiamondForExclusivePrivate
                ? addvalue()
                : unlockPrivateStream({ streamId });
            }}
          >
            {t(
              isInsufficientDiamondForExclusivePrivate
                ? 'button_add_funds_for_stream'
                : 'button_enter_exclusive',
              {
                ns: TranslationNamespace.LIVESTREAM,
              }
            )}
          </PurchaseDiamondButton>
        }
      />
    );
  }

  const renderLivestreamOverlayInfo = () => (
    <Body>
      <Header>
        {streamTitle ||
          t('title_in_private_stream', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
      </Header>
      {categories.length > 0 && (
        <CategoriesWrapper>
          {categories.map(category => (
            <CategoryItem key={category}>
              {t(
                getTranslationKey({
                  key: `livestream_settings_tags_${category}`,
                }),
                {
                  ns: TranslationNamespace.LIVESTREAM,
                }
              )}
            </CategoryItem>
          ))}
        </CategoriesWrapper>
      )}
      <WithLivestreamRating id={streamId}>
        {({
          ratingStepSize,
          displayRating,
          displayRatingCount,
          rating,
          isDisplayRatingEnabled,
        }) =>
          !!rating && isDisplayRatingEnabled ? (
            <RatingSummary>
              <DisplayRating>{displayRating}</DisplayRating>
              <Rating
                value={displayRating || 0}
                max={100 / ratingStepSize}
                iconSize={20}
                isReadOnly
              />
              <DisplayRatingCount>{displayRatingCount}</DisplayRatingCount>
            </RatingSummary>
          ) : null
        }
      </WithLivestreamRating>
      <Footer>{renderFooterContent()}</Footer>
      <SnapshotPicture>
        {coverSource && (
          <DecryptionWrapper resourceUrl={coverSource}>
            {[MediaAssetFormat.AVIF, MediaAssetFormat.WEBP].map(format => (
              <source
                key={format}
                type={`image/${format}`}
                srcSet={
                  getPublicSrcSet({
                    href: coverSource,
                    size: 512,
                    format,
                  }).srcSet
                }
              />
            ))}
            <img
              {...getPublicSrcSet({
                href: coverSource,
                size: 512,
                format: MediaAssetFormat.JPG,
              })}
              alt={`${username}'s livestream cover`}
            />
          </DecryptionWrapper>
        )}
      </SnapshotPicture>
    </Body>
  );

  const isPrivateShow = LivestreamMode.SHOW === mode;

  const renderFooterContent = () => (
    <LivestreamOverlayFooter
      streamId={streamId}
      isPrivateShow={isPrivateShow}
      mixpanelModalId={mixpanelModalId}
      shouldShowCountdownWhenPerforming={isPrivateShow}
    />
  );

  const renderLivestreamOverlayChatroom = () => {
    if (deviceWidth > breakpoint.tablet) return null;
    return (
      <LivestreamOverlayChatroom
        streamId={streamId}
        footerContent={renderFooterContent()}
      />
    );
  };

  const slides = [
    {
      key: 'overlay-info',
      content: renderLivestreamOverlayInfo(),
    },
    {
      key: 'chatroom',
      content: renderLivestreamOverlayChatroom(),
    },
  ].filter(item => item.content != null);

  return (
    <StyledLivestreamOverlayCta>
      <LivestreamOverlaySwiper>
        {slides.map(({ key, content }) => (
          <SwiperSlide key={key}>{content}</SwiperSlide>
        ))}
      </LivestreamOverlaySwiper>
    </StyledLivestreamOverlayCta>
  );
};

LivestreamOverlayCta.propTypes = {
  streamId: PropTypes.string,
  meId: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  mode: PropTypes.string,
  streamTitle: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.string),
  balance: PropTypes.number,
  exclusivePrice: PropTypes.number,
  addvalue: PropTypes.func,
  unlockPrivateStream: PropTypes.func,
  fetchStreamsToken: PropTypes.func,
  setModalTracking: PropTypes.func,
  sessionId: PropTypes.string,
  isLiveStreamSnapshotEnabled: PropTypes.bool,
  publicUrlHostname: PropTypes.string,
  isExclusivePrivatePerforming: PropTypes.bool,
  isExclusivePrivatePreparing: PropTypes.bool,
  isMeExclusivePrivateBuyer: PropTypes.bool,
  hasStreamToken: PropTypes.bool,
  isFetchingStreamToken: PropTypes.bool,
  deviceWidth: PropTypes.number,
};

const StyledLivestreamOverlayCta = styled.div`
  width: 443.25px;
  overflow: hidden;
  ${media.tablet`
    width: 100vw;
  `}
`;

const SnapshotPicture = styled.picture`
  position: absolute;
  z-index: -1;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  background-color: ${color.black};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
  }
  ::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0.05) 16.68%,
      rgba(0, 0, 0, 0.3) 52.99%,
      rgba(0, 0, 0, 0.6) 100%
    );
  }
`;

const Header = styled.div`
  min-height: 27px;
  display: flex;
  align-items: left;
  justify-content: left;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 600;
  color: ${color.neutral[100]};
`;

const CategoriesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const CategoryItem = styled.div`
  display: inline-block;
  border-radius: 40px;
  padding: 2px 8px;

  color: ${color.neutral['100']};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;

  background-color: ${color.neutral['85_alpha_20']};
`;

const Body = styled.div`
  position: relative;
  display: grid;
  align-content: end;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 16px;
  height: 100%;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const RatingSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: left;
`;

const DisplayRating = styled.div`
  margin-right: 4px;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.5;
  color: ${color.neutral[100]};
`;

const DisplayRatingCount = styled.div`
  margin-left: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: ${color.neutral[75]};
  ::before {
    content: '(';
  }
  ::after {
    content: ')';
  }
`;

const FlexCenterBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 8px;
`;

const UserAvatarWrapper = styled.div`
  padding: 6px;
  margin-left: -24px;
  :first-child {
    margin-left: 0;
    z-index: 1;
  }
`;

const UserAvatarRow = styled.div`
  display: flex;
`;

const UserDisplayName = styled.div`
  display: flex;
  align-items: center;
  color: white;
  text-align: center;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 24px */
  letter-spacing: 0.012px;
  font-size: 14px;
  ${media.mobile`
    font-size: 12px;
  `}
`;

const PurchaseDiamondButton = styled.button`
  display: flex;
  height: 40px;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid ${color.tealBlue};

  color: ${color.tealBlue};
  font-weight: 600;
  line-height: 150%; /* 30px */
  letter-spacing: 0.02px;
  font-size: 20px;
  :disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  ${media.mobile`
    font-size: 18px;
  `}
`;

export default LivestreamOverlayCta;
